"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import { motion } from "framer-motion";
import {
  BarChart3,
  Boxes,
  CheckCircle2,
  Database,
  FileSearch,
  FileText,
  Gauge,
  Globe,
  Lock,
  ScanText,
  ShieldCheck,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import * as React from "react";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import apiImage from "@/public/images/api.jpg";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

const EXTRACT_SNIPPET = `curl -X POST https://api.kautio.us/extract \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Acme Corp CFO Jane Doe sold 12,000 shares at $48.20 on March 3.",
    "prompt": "Extract insider transactions with person, role, shares, price, and date.",
    "include_source_grounding": true
  }'`;

export default function ExtractPage() {
  const { theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isLight = mounted && theme === "light";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        {/* Hero Section with Background */}
        <section className="relative w-full overflow-hidden min-h-[85vh] flex items-center justify-center">
          {/* Dynamic Background Image */}
          <div className="absolute inset-0 z-0">
            <Image
              src={apiImage}
              alt="Document extraction visualization"
              className="object-cover object-center w-full h-full opacity-40 dark:opacity-30"
              fill
              priority
              sizes="100vw"
              quality={90}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-indigo-900/40 to-background/95" />
          </div>

          {/* Background Pattern */}
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_80%)] pointer-events-none z-10 opacity-30" />

          {/* Floating Animated Icons */}
          <motion.div
            className="absolute top-20 left-8 w-16 h-16 bg-blue-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-blue-500/20 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <FileText className="w-8 h-8 text-blue-500/70" />
          </motion.div>
          <motion.div
            className="absolute top-32 right-12 w-14 h-14 bg-indigo-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-indigo-500/20 z-10"
            style={{ animationDelay: "1s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ScanText className="w-7 h-7 text-indigo-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-32 left-16 w-14 h-14 bg-cyan-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-cyan-500/20 z-10"
            style={{ animationDelay: "2s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Gauge className="w-7 h-7 text-cyan-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-20 right-20 w-14 h-14 bg-sky-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-sky-500/20 z-10"
            style={{ animationDelay: "0.5s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <CheckCircle2 className="w-7 h-7 text-sky-500/70" />
          </motion.div>

          {/* Hero Content */}
          <div className="relative z-20 section-container">
            <div className="flex flex-col items-center text-center max-w-5xl mx-auto px-4 py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span
                  className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
                  style={{
                    backgroundColor: isLight
                      ? "rgba(17, 24, 39, 0.2)"
                      : "rgba(255, 255, 255, 0.2)",
                    color: isLight ? "#111827" : "#ffffff",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isLight
                      ? "rgba(17, 24, 39, 0.3)"
                      : "rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <FileSearch
                    className="w-4 h-4 mr-2"
                    style={{ color: isLight ? "#111827" : "#ffffff" }}
                  />
                  Kautious Extract
                </span>
                <h1
                  className="mb-6"
                  style={{ color: isLight ? "#111827" : "#ffffff" }}
                >
                  Stop paying experts to copy data out of documents.
                </h1>
                <p
                  className="text-xl mb-10 leading-relaxed max-w-4xl"
                  style={{
                    color: isLight ? "#374151" : "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Kautious Extract turns SEC filings, scanned PDFs, tables, web
                  pages, and transcripts into quality-scored, source-grounded
                  JSON through a five-tier parser stack. Put your experts on
                  judgment — not OCR cleanup.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button
                    size="lg"
                    className="hover:opacity-90"
                    style={{
                      backgroundColor: isLight ? "#2563eb" : "#3b82f6",
                      color: "#ffffff",
                    }}
                    asChild
                  >
                    <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                      Bring Us a Workflow
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="hover:opacity-80"
                    style={{
                      borderColor: isLight ? "#111827" : "#ffffff",
                      color: isLight ? "#111827" : "#ffffff",
                      backgroundColor: "transparent",
                    }}
                    asChild
                  >
                    <Link href="#see-it-work">See it work</Link>
                  </Button>
                </div>
                <p
                  className="mt-6 text-sm"
                  style={{
                    color: isLight ? "#4b5563" : "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  Hands-on onboarding for teams with a live extraction workload.
                  Every engagement starts with a production workflow, not a
                  generic demo.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section className="section-padding bg-secondary/5">
          <div className="section-container max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Manual extraction is overhead. Bad extraction is risk.
              </h2>
              <p className="text-lg text-muted-foreground">
                You know the lifecycle because you have lived it:
              </p>
            </div>
            <div className="space-y-4">
              {[
                "Someone writes a parser. It works on the demo file.",
                "A scanned document arrives. You bolt on OCR.",
                "A table breaks. You bolt on another parser.",
                "Someone asks, “where did this number come from?” and nobody has a clean answer.",
                "The person who built it leaves. The workflow becomes institutional folklore.",
              ].map((text, i) => (
                <div
                  key={text}
                  className="flex items-start gap-4 p-5 rounded-lg bg-background border border-border/50"
                >
                  <div className="flex-shrink-0 w-9 h-9 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <p className="text-foreground pt-1">{text}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-lg text-foreground mt-10 max-w-3xl mx-auto">
              Kautious Extract is built on the opposite assumption: parsers
              fail, source formats drift, and every useful result has to be
              observable, repeatable, and reviewable from the beginning.
            </p>
          </div>
        </section>

        {/* See it work */}
        <section id="see-it-work" className="section-padding">
          <div className="section-container max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                From raw document to source-grounded JSON.
              </h2>
            </div>
            <div className="rounded-xl border border-border bg-muted/40 overflow-hidden mb-8">
              <div className="px-4 py-2 border-b border-border bg-muted/60 text-xs font-mono text-muted-foreground">
                POST /extract
              </div>
              <pre className="p-5 overflow-x-auto text-sm">
                <code className="font-mono text-foreground/90">
                  {EXTRACT_SNIPPET}
                </code>
              </pre>
            </div>
            <p className="text-center text-muted-foreground mb-4">
              No brittle script. No one-off spreadsheet. No mystery output. Need
              a schema first?{" "}
              <code className="font-mono text-sm">POST /auto-schema</code> reads
              representative documents and drafts entity types, prompts, and
              examples.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {[
                "PDF",
                "Word",
                "Excel",
                "PowerPoint",
                "Scanned images",
                "EPUB",
                "ZIP",
                "CSV/TSV",
                "XML",
                "Jupyter notebooks",
                "Live web pages",
                "Audio transcription",
              ].map((fmt) => (
                <span
                  key={fmt}
                  className="text-sm text-foreground/80 border border-border bg-background rounded-full px-3 py-1.5"
                >
                  {fmt}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* The Buyer */}
        <section className="section-padding bg-muted/30">
          <div className="section-container">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Your experts were hired to judge the facts, not hunt for them.
              </h2>
              <p className="text-lg text-muted-foreground">
                You are buying the version of your workflow that is already
                running, monitored, and defensible.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {[
                {
                  title: "When the auditor asks where a value came from",
                  body: "You have source grounding instead of hand waving. Extracted entities point back to character ranges in the original text.",
                },
                {
                  title: "When the boss asks if the AI is accurate",
                  body: "You show the quality score. Every extraction is graded across five dimensions, and model comparison explains why one configuration won.",
                },
                {
                  title: "When the ugly document shows up",
                  body: "The system has a plan. The parser chain moves through local OCR, cloud OCR, agentic parsing, PDF fallback, and broad-format conversion.",
                },
                {
                  title: "When everything must work in ChatGPT",
                  body: "You are not starting from scratch. The same capabilities are exposed through MCP tools, OAuth, scheduled jobs, and interactive widgets.",
                },
              ].map((item) => (
                <Card key={item.title} className="p-8 card-hover">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">
                        {item.body}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Platform */}
        <section className="section-padding">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Five parsers. One defensible result.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: Database,
                  title: "Ingestion that expects failure",
                  body: "A five-tier parser stack — LiteParse, Datalab Marker, LlamaParse, pypdf, and MarkItDown. Pick a parser when precision matters, or let the registry fall through automatically.",
                },
                {
                  icon: Globe,
                  title: "Web extraction for the modern internet",
                  body: "Extract from URLs with provider-aware acquisition through Stagehand/Browserbase, OloStep, and Spider.cloud. Render pages, capture HTML tables, fall back when needed.",
                },
                {
                  icon: BarChart3,
                  title: "Quality controls operators can use",
                  body: "Quality scoring, model comparison, extraction summaries, cache metadata, operation history, streaming progress, and visualizations — inspect work instead of trusting a black box.",
                },
                {
                  icon: Boxes,
                  title: "One workflow across surfaces",
                  body: "A FastAPI service for product integration, a React dashboard for operators, MCP tools for ChatGPT/OpenAI Apps, and Convex-backed metadata for scheduled jobs and batches.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={item.title}
                    className="p-8 card-hover border-l-4 border-l-blue-500"
                  >
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Domain Packs */}
        <section className="section-padding bg-muted/30">
          <div className="section-container max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Generic extraction is a head start. Domains are a finish line.
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Kautious Extract ships domain templates, schemas, validators,
                and connectors for workflows where wrong answers get expensive.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: "SEC",
                  body: "Filing search, sections, XBRL facts, company lookup, and per-filing extraction.",
                },
                {
                  name: "Fed & rates",
                  body: "Federal Reserve material, yield curves, reference rates, and auction data.",
                },
                {
                  name: "Market",
                  body: "Corporate actions, earnings, M&A, and market-event extraction.",
                },
                {
                  name: "Regulatory",
                  body: "FINRA and CFPB enforcement workflows.",
                },
                {
                  name: "KYC & audit",
                  body: "Sanctions, beneficial ownership, and financial-statement footnote templates.",
                },
                {
                  name: "Inbox & meeting",
                  body: "Email triage and meeting-intelligence templates for analyst workflows.",
                },
              ].map((item) => (
                <Card key={item.name} className="p-6 card-hover">
                  <h3 className="font-semibold mb-2">{item.name}</h3>
                  <p className="text-sm text-muted-foreground">{item.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Security */}
        <section className="section-padding">
          <div className="section-container max-w-4xl mx-auto text-center">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Lock className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Hardened before you asked.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              This audience reads vague security language as a red flag. The
              security review happened before launch, not after the incident.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "SSRF-protected URL ingestion",
                "Request-size limits at the ASGI layer",
                "OAuth 2.1 + JWT auth",
                "Rate limiting",
                "Provider-aware proxy controls",
                "Dependency auditing in CI",
                "OpenTelemetry tracing",
                "Prometheus metrics",
                "Convex-backed audit records",
              ].map((item) => (
                <span
                  key={item}
                  className="text-sm text-foreground/80 border border-border bg-background rounded-full px-4 py-2"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Get Started CTA */}
        <section className="section-padding bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
          <div className="section-container text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-4 text-white/90">
                <ShieldCheck className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-[0.2em]">
                  Production workflow onboarding
                </span>
              </div>
              <h2 className="text-4xl font-semibold mb-6 text-white">
                Turn document backlog into decision-ready data.
              </h2>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Bring us the documents your current stack cannot handle. If it
                is a fit, we&apos;ll build the extraction path around the real
                workflow.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-gray-100"
                  asChild
                >
                  <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                    Bring Us a Workflow
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link href="/daytta">See it inside Daytta</Link>
                </Button>
              </div>
              <p className="mt-8 text-sm text-white/70">
                Design partners in financial services, compliance, legal, and AI
                product teams get priority.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
